import { createMemoryHistory, type RouterHistory } from '@tanstack/react-router';

/**
 * Host ↔ remote route sync for federated remotes. The aistudio host's
 * vue-router owns the browser URL; the remote runs on a memory history.
 * Counterpart of aistudio's `useFederatedRemote` composable: every path
 * exchanged with the host is relative to the host base the remote is mounted
 * under, and both sides no-op when already on the target path, which breaks
 * the sync loop.
 *
 * `internalBase` is the prefix the remote's route tree expects ('' when its
 * route paths are already host-relative). Internal navigations outside it are
 * not reported, leaving the host URL unchanged.
 */

export interface FederationRoutingProps {
  basePath?: string;
  initialPath?: string;
  onNavigate?: (hostRelativePath: string) => void;
}

export interface SyncableRouter {
  state: { location: { pathname: string; searchStr: string } };
  history: RouterHistory;
  subscribe(
    eventType: 'onResolved',
    listener: (event: { toLocation: { pathname: string; searchStr: string } }) => void
  ): () => void;
}

export function createFederationRouterSync<TRouter extends SyncableRouter>(options: {
  routing?: FederationRoutingProps;
  internalBase?: string;
  createRouter: (history: RouterHistory) => TRouter;
}) {
  const { routing, internalBase: rawInternalBase = '', createRouter } = options;
  const internalBase = rawInternalBase.endsWith('/')
    ? rawInternalBase.slice(0, -1)
    : rawInternalBase;

  const toInternal = (hostPath: string): string =>
    `${internalBase}${hostPath === '/' ? '' : hostPath}` || '/';

  const toHost = (internalPath: string): string | null => {
    if (!internalBase) return internalPath;
    if (internalPath === internalBase) return '/';
    if (
      internalPath.startsWith(`${internalBase}/`) ||
      internalPath.startsWith(`${internalBase}?`)
    ) {
      return internalPath.slice(internalBase.length);
    }
    return null;
  };

  const router = createRouter(
    createMemoryHistory({ initialEntries: [toInternal(routing?.initialPath ?? '/')] })
  );
  const currentPath = () => router.state.location.pathname + router.state.location.searchStr;

  const onNavigate = routing?.onNavigate;
  const unsubscribe = onNavigate
    ? router.subscribe('onResolved', ({ toLocation }) => {
        const hostPath = toHost(toLocation.pathname + toLocation.searchStr);
        if (hostPath) onNavigate(hostPath);
      })
    : null;

  return {
    router,
    onParentNavigate(hostRelativePath: string) {
      // replace(), not push(): this is the host's vue-router mirroring its own
      // (possibly back/forward-driven) location onto the remote, not a new
      // navigation the remote initiated. push() would let the host's back
      // button leave stale entries on the remote's own history stack — a
      // later router.history.back() inside the remote could then land on one
      // of those and re-trigger a host update for a route the user never
      // actually visited from here.
      const target = toInternal(hostRelativePath);
      if (currentPath() !== target) router.history.replace(target);
    },
    dispose() {
      unsubscribe?.();
    },
  };
}
